import useSnackbar from '@/composables/useSnackbar';
import { useAuthStore } from '@/modules/auth/store/auth';
import { useCartStore } from '@/store/cart';
import { CART_INCLUDES } from '@/types/Cart';
import type { Cart } from '@/types/Cart';
import type { Product } from '@/types/Product';

const fetchCartsInFlight = ref(false);

const buildCartListUrls = (customerAccountableId: number | null): string[] => {
  const includeLevels = [
    CART_INCLUDES,
    'cartProducts.photos',
    'cartProducts',
    'cart_products.photos,cart_products.variant',
    'cart_products.photos',
    'cart_products',
  ] as const;
  const out: string[] = [];
  for (const inc of includeLevels) {
    const withInc = new URLSearchParams({ include: inc });
    const baseOnly = `/carts?${withInc.toString()}`;
    if (customerAccountableId != null) {
      const filtered = new URLSearchParams(withInc);
      filtered.set('filter[customer_id]', String(customerAccountableId));
      out.push(`/carts?${filtered.toString()}`);
    }
    out.push(baseOnly);
  }
  out.push('/carts');
  return [...new Set(out)];
};

export const useCart = () => {
  const { showSnackbar } = useSnackbar();
  const cartStore = useCartStore();
  const { carts, cartsLoaded, activeCart } = storeToRefs(cartStore);
  const authStore = useAuthStore();
  const router = useRouter();

  const loading = ref(false);

  const mergeProductVariantsForLookup = (product: Product) => {
    const merged: NonNullable<Product['variants']> = [];
    const seen = new Set<number>();
    const push = (list?: Product['variants']) => {
      for (const v of list ?? []) {
        if (!seen.has(v.id)) {
          seen.add(v.id);
          merged.push(v);
        }
      }
    };
    push(product.active_variants);
    push(product.activeVariants);
    push(product.variants);
    const activeOnly = merged.filter((v) => v.is_active !== false);
    return activeOnly.length ? activeOnly : merged;
  };

  // get carts
  const { data: dataCarts, execute: getCarts, error: getCartsError } = useGetData<{ data: Cart[] }>();

  // get single
  const { data: dataSingleCart, execute: getSingleCart } = useGetData<{
    data: Cart;
  }>();

  // create cart
  const { data: createdCartData, execute: executeCartCreate } = useSubmit<{
    data: Cart;
  }>();

  // update cart
  const {
    data: updatedCartData,
    execute: executeCartUpdate,
    pending: cartLoading,
  } = useSubmit<{ data: Cart }>();

  const fetchCarts = async () => {
    if (fetchCartsInFlight.value) {
      return;
    }
    fetchCartsInFlight.value = true;
    loading.value = true;
    try {
      if (authStore.isCustomer) {
        const aid = authStore.user?.accountable_id ?? null;
        const attempts = buildCartListUrls(aid);
        for (const url of attempts) {
          await getCarts(url);
          if (!getCartsError.value) {
            break;
          }
        }
        carts.value = dataCarts.value?.data || [];
      } else {
        carts.value = [];
      }
    } finally {
      loading.value = false;
      cartsLoaded.value = true;
      fetchCartsInFlight.value = false;
    }
  };

  const fetchSingleCart = async (
    id: number | string,
    latest: boolean = false
  ) => {
    activeCart.value = undefined;
    loading.value = true;

    if (!latest) {
      const cart = carts.value.find((cart) => cart.id == id);
      if (cart) {
        activeCart.value = cart;
        loading.value = false;

        return cart;
      }
    }

    await getSingleCart(`/carts/${id}?include=${CART_INCLUDES}`);

    loading.value = false;
    activeCart.value = dataSingleCart.value?.data;

    return dataSingleCart.value?.data;
  };

  const addToCart = async (
    product: Product,
    quantity: number,
    productVariantId?: number | null
  ) => {
    if (product.has_variants) {
      if (productVariantId == null || Number.isNaN(Number(productVariantId))) {
        showSnackbar({
          text: 'Please select a product option before adding this item to your cart.',
          state: false,
        });
        return;
      }
      const variants = mergeProductVariantsForLookup(product);
      const variant = variants.find((row) => row.id === productVariantId);
      if (!variant || variant.quantity <= 0) {
        showSnackbar({
          text: 'This item is out of stock.',
          state: false,
        });
        return;
      }
    } else if (product.quantity <= 0) {
      showSnackbar({
        text: 'This item is out of stock.',
        state: false,
      });
      return;
    }

    cartLoading.value = true;

    const task = async () => {
      let cart = cartStore.carts.find(
        (cart) => cart.merchant_id === product?.seller?.accountable_id
      );

      if (!cart) {
        await executeCartCreate(`/carts`, {
          method: 'POST',
          body: { merchant_id: product?.seller?.accountable_id },
        });

        if (createdCartData.value) {
          cart = createdCartData.value.data;
          cartStore.addCartStore(cart);
        }
      }

      if (cart) {
        const body: {
          product_id: number;
          quantity: number;
          product_variant_id?: number;
        } = {
          product_id: product.id,
          quantity,
        };
        if (product.has_variants) {
          body.product_variant_id = productVariantId as number;
        }

        await executeCartUpdate(`/carts/${cart?.id}/add-product`, {
          method: 'POST',
          body,
        });
      }

      if (updatedCartData.value) {
        activeCart.value = updatedCartData.value?.data;
        cartStore.updateCartStore(activeCart.value);
      }

      cartLoading.value = false;
    };

    await cartStore.addQueuedTask(task);
  };

  const removeCart = async (cart_id: number) => {
    await executeCartUpdate(`/carts/${cart_id}`, {
      method: 'DELETE',
    });

    cartStore.removeCartStore(cart_id);
  };

  const removeToCart = async (
    cart_id: number,
    product_id: number,
    product_variant_id?: number | null
  ) => {
    loading.value = true;

    await executeCartUpdate(`/carts/${cart_id}/remove-product`, {
      method: 'DELETE',
      body: {
        product_id,
        ...(product_variant_id != null ? { product_variant_id } : {}),
      },
    });

    if (updatedCartData.value) {
      activeCart.value = updatedCartData.value?.data;
      cartStore.updateCartStore(activeCart.value);
    }

    // remove the cart when no items left
    if (activeCart.value && activeCart.value?.products.length === 0) {
      await removeCart(activeCart.value.id as number);
      router.back();
    }

    loading.value = false;
  };

  const updateQuantity = async (
    cart_id: number,
    product_id: number,
    quantity: number,
    product_variant_id?: number | null
  ) => {
    loading.value = true;

    await executeCartUpdate(`/carts/${cart_id}/update-product-quantity`, {
      method: 'PUT',
      body: {
        product_id,
        quantity,
        ...(product_variant_id != null ? { product_variant_id } : {}),
      },
    });

    if (updatedCartData.value) {
      activeCart.value = updatedCartData.value?.data;
      cartStore.updateCartStore(activeCart.value);
    }

    loading.value = false;
  };

  return {
    activeCart,
    loading,
    cartLoading,
    fetchCarts,
    fetchSingleCart,
    addToCart,
    removeToCart,
    updateQuantity,
  };
};
